import { select, call, takeLatest } from 'redux-saga/effects';
import { ServiceLocationActions } from '../constants';
import { ServiceLocationApi } from 'src/services/end-points';
import { StoreState } from '../reducers';
import { PaginationService } from '../../services/api-handlers/pagination';
import {
    DeleteServiceLocationAction,
    GetServiceLocationAction,
    GetServiceLocationsAction,
    PersistServiceLocationAction,
} from '../actions/service-location-actions';
import sortingService from '../../services/sorting';
import { resolveApiCall, ResolverApiFailure, handleError } from '../../services/api-handlers/api-resolver';
import { SubmissionError } from 'redux-form';

function* handleFetchServiceLocations(action: GetServiceLocationsAction) {
    const oldData = yield select((state: StoreState) => state.serviceLocations.data);
    const { filters, sorting, page, itemsPerPage } = action.payload;

    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        ServiceLocationApi.getServiceLocations({
            ...filters,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}

function* handleFetchServiceLocation(action: GetServiceLocationAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => ServiceLocationApi.getServiceLocation(id));
}

function* handleDeleteServiceLocation(action: DeleteServiceLocationAction) {
    const {
        payload: { id, onSuccess, onError },
    } = action;
    yield resolveApiCall(
        action,
        () => ServiceLocationApi.deleteServiceLocation(id),
        function () {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

function* handlePersistServiceLocation(action: PersistServiceLocationAction) {
    const { payload } = action;
    const {
        data: { id, ...restData },
        callbacks,
    } = payload;

    yield resolveApiCall(
        action,
        () =>
            id
                ? ServiceLocationApi.updateServiceLocation(id, restData)
                : ServiceLocationApi.createServiceLocation(restData),
        function* () {
            yield call(callbacks.resolve);
        },
        function* (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                return yield call(
                    callbacks.reject,
                    result.error instanceof SubmissionError
                        ? result.error
                        : new SubmissionError({
                              _error: result.error.message ?? 'Incorrect response',
                          }),
                );
            }
            yield handleError(result);
        },
    );
}

export default function* () {
    yield takeLatest(ServiceLocationActions.STAFF_FETCH_SERVICE_LOCATIONS, handleFetchServiceLocations);
    yield takeLatest(ServiceLocationActions.STAFF_FETCH_SERVICE_LOCATION, handleFetchServiceLocation);
    yield takeLatest(ServiceLocationActions.STAFF_DELETE_SERVICE_LOCATION, handleDeleteServiceLocation);
    yield takeLatest(ServiceLocationActions.STAFF_PERSIST_SERVICE_LOCATION, handlePersistServiceLocation);
}
